/**
 * Google Analytics 4 — pageview + event telemetry.
 *
 * Loaded once from main.tsx when `VITE_GA_MEASUREMENT_ID` is set at build
 * time; otherwise this module is a no-op (`gtag` calls quietly drop). The
 * narrowed privacy claim is "your receipts never leave your device" —
 * GA never sees receipt content. Events emitted from the app pass only
 * non-PII counters: format names like `csv` / `xlsx` / `zip`, the
 * detected scan-correct method, etc. NEVER vendor / total / filename /
 * OCR text.
 *
 * The CSP in `nginx.conf` allowlists `*.googletagmanager.com` (script
 * load) and `*.google-analytics.com` (collect endpoint) for exactly
 * this path.
 */

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

let initialised = false;

/**
 * Inject gtag.js and configure the property. Idempotent. Call once per
 * session — `main.tsx` is the only invocation site.
 */
export function initAnalytics(): void {
  if (initialised) return;
  if (GA_ID === undefined || GA_ID.trim() === '') return;
  initialised = true;

  // Standard gtag.js bootstrap pattern from Google's quickstart, adapted
  // to TypeScript: dataLayer + gtag function + script tag. The function
  // form is required so the gtag calls before the async script loads
  // get queued instead of dropped.
  window.dataLayer = window.dataLayer ?? [];
  // Canonical Google snippet pushes the `arguments` object — gtag.js's
  // queue processor treats Arguments-shaped entries as commands and
  // silently skips real Arrays. A rest-params bootstrap (push(args))
  // therefore looks identical in DevTools but leaves GA4 unconfigured
  // and stops every /collect beacon from firing.
  function gtag(..._args: unknown[]): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag;
  // GA4 Consent Mode v2: under EEA traffic + default-denied property
  // settings, gtag silently drops every beacon until it sees a granted
  // consent signal. We only call initAnalytics() AFTER the user clicks
  // Accept on our cookie banner, so granting all four is correct here —
  // the banner IS the consent gate.
  gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
  gtag('js', new Date());
  gtag('config', GA_ID, {
    // GA's automatic page_view event is fine for the SPA — there are
    // no real route changes. Keep send_page_view default true.
    anonymize_ip: true,
  });

  const script = document.createElement('script');
  script.async = true;
  // CORS-load the script. Mirrors ads.ts and is harmless even with
  // COEP disabled — gtag/js returns Access-Control-Allow-Origin: *.
  script.crossOrigin = 'anonymous';
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
  document.head.appendChild(script);
}

/**
 * Emit a non-PII event. Receipt content (vendor, total, filename, OCR
 * text) MUST NOT pass through here — that would defeat the privacy
 * claim. Use it only for counters / category names.
 */
export function trackEvent(name: string, params?: Record<string, string | number>): void {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', name, params ?? {});
}
